import { createReducer } from '@cascade/core/utils';
import type {
  TConditionalWidgetEntry,
  TExperienceAction,
  TExperienceState,
  TExperienceTransition,
} from './useExperienceState.types.ts';
import {
  areAnswersEqual,
  clearHiddenAnswers,
  getSeededAnswers,
  getStepWidgets,
} from '#providers/experiences/Experience.helpers.ts';

export const createInitialState = (): TExperienceState => ({
  visitedStepIds: [],
  answers: {},
  widgets: {},
  hasUnsavedAnswers: false,
});

export const restoreState = (
  storedState?: TExperienceState,
): TExperienceState => storedState ?? createInitialState();

const openStep: TExperienceTransition<'step.opened'> = (
  state,
  { fromStepId, toStepId },
) => {
  const visitedStepIds =
    state.visitedStepIds.length > 0 || !fromStepId
      ? state.visitedStepIds
      : [fromStepId];

  if (visitedStepIds.at(-1) === toStepId) {
    return state;
  }

  return { ...state, visitedStepIds: [...visitedStepIds, toStepId] };
};

const loadStep: TExperienceTransition<'step.loaded'> = (state, { step }) => {
  const stepWidgets = getStepWidgets(step);
  const isStepKnown = stepWidgets.every(
    (widget) => state.widgets[widget.id] === widget,
  );

  if (isStepKnown) {
    return state;
  }

  const stepWidgetEntries = stepWidgets.map(
    (widget): TConditionalWidgetEntry => [widget.id, widget],
  );

  const widgets = {
    ...state.widgets,
    ...Object.fromEntries(stepWidgetEntries),
  };

  const knownWidgets = Object.values(widgets);
  const seededAnswers = { ...getSeededAnswers(stepWidgets), ...state.answers };
  const clearedAnswers = clearHiddenAnswers(seededAnswers, knownWidgets);
  const hasClearedAnswers = clearedAnswers !== seededAnswers;
  const answers = areAnswersEqual(clearedAnswers, state.answers)
    ? state.answers
    : clearedAnswers;

  return {
    ...state,
    widgets,
    answers,
    hasUnsavedAnswers: state.hasUnsavedAnswers || hasClearedAnswers,
  };
};

const setAnswer: TExperienceTransition<'answer.set'> = (
  state,
  { widgetId, value },
) => {
  const knownWidgets = Object.values(state.widgets);
  const changedAnswers = { ...state.answers, [widgetId]: value };
  const answers = clearHiddenAnswers(changedAnswers, knownWidgets);

  if (areAnswersEqual(answers, state.answers)) {
    return state;
  }

  return { ...state, answers, hasUnsavedAnswers: true };
};

const markSaved: TExperienceTransition<'answers.saved'> = (
  state,
  { answers },
) => ({
  ...state,
  hasUnsavedAnswers: !areAnswersEqual(state.answers, answers),
});

export const reduceExperience = createReducer<
  TExperienceState,
  TExperienceAction
>({
  'step.opened': openStep,
  'step.loaded': loadStep,
  'answer.set': setAnswer,
  'answers.saved': markSaved,
});
